import { IconButton, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, type ReactNode } from "react";
import type { RawCheckpointView, TrainingShareLinkTarget } from "@/api";
import type { LoadState } from "@/components/dataset";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatDateTime, formatDateTimeSeconds, parseUtcMs } from "@/utils/datetime";
import { downloadSessionRawCheckpoint } from "../sessionCheckpointDownload";
import {
  SessionArtifactDownloadButton,
  SessionArtifactRow,
  sessionArtifactFileColumns,
  useSessionArtifactDownload,
} from "./sessionArtifactTable";
import { checkpointDisplayLabel } from "./trainingSessionDetailFormat";
import styles from "./TrainingSessionDetailPanels.module.css";

type RawCheckpointRow = SessionArtifactRow<RawCheckpointView>;

// 체크포인트 탭의 두 표(Exported·Raw)가 함께 쓰는 공유 발급 핸들. 패널이 발급 훅 하나로 만든다.
export type CheckpointShareAction = {
  // 발급 중인 행 id. 발급하는 동안 두 표의 공유 버튼을 모두 잠근다.
  pendingKey: string | null;
  share: (key: string, target: TrainingShareLinkTarget) => void;
};

// Exported 목록이 비거나 실패해도 실제 Raw 파일을 볼 수 있도록 자체 상태를 표시한다.
export function RawCheckpointsSection({
  sessionId,
  state,
  retry,
  share,
}: {
  sessionId: string;
  state: LoadState<RawCheckpointView[]>;
  retry: () => void;
  share: CheckpointShareAction;
}): ReactNode {
  const download = useSessionArtifactDownload((file: RawCheckpointView) =>
    downloadSessionRawCheckpoint(sessionId, file),
  );
  const rows = useMemo(
    () =>
      state.status === "success" ? state.data.map((file) => new SessionArtifactRow(file)) : [],
    [state],
  );
  const source = useMemo(() => new StaticTableSource(rows), [rows]);
  useEffect(() => () => source.dispose(), [source]);

  const columns: readonly DataTableColumn<RawCheckpointRow>[] = [
    {
      key: "updatedAt",
      header: "갱신 시각 / Checkpoint",
      width: "minmax(170px, 1fr)",
      render: ({ file }) => (
        <span className={styles.rawCell}>
          <HatTextTooltip text={formatDateTimeSeconds(parseUtcMs(file.updated_at))}>
            <span>{formatDateTime(parseUtcMs(file.updated_at))}</span>
          </HatTextTooltip>
          <span className={styles.cardMeta}>
            {file.checkpoint_tag === "last" ? "LAST" : checkpointDisplayLabel(file.checkpoint_tag)}
          </span>
        </span>
      ),
    },
    ...sessionArtifactFileColumns<RawCheckpointView>(),
    {
      key: "file",
      header: "동작",
      // 다운로드(스피너 포함)와 공유 두 버튼.
      width: "184px",
      align: "right",
      render: (row) => (
        <span className={styles.checkpointActions}>
          <SessionArtifactDownloadButton
            rowId={row.getRowId()}
            pendingRowId={download.pendingRowId}
            onClick={() => download.start(row)}
          />
          <IconButton
            text="공유"
            size="sm"
            variant="surface"
            title="로그인 없이 받을 수 있는 1일짜리 공유 링크를 복사합니다."
            disabled={share.pendingKey !== null}
            onClick={() =>
              share.share(row.getRowId(), {
                kind: "raw_checkpoint",
                session_id: sessionId,
                run_id: row.file.run_id,
                artifact_path: row.file.artifact_path,
              })
            }
          />
        </span>
      ),
    },
  ];

  return (
    <section className={styles.rawSection} aria-label="Raw Checkpoint">
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Raw Checkpoint</h3>
        <p className={styles.muted}>저장된 .cb6pth 파일을 갱신 시각이 최신인 순서로 표시합니다.</p>
      </div>
      {state.status === "idle" || state.status === "loading" ? (
        <LoadingState size="sm">Raw Checkpoint를 불러오고 있습니다.</LoadingState>
      ) : state.status === "error" ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: retry }}>
          Raw Checkpoint 목록을 불러오지 못했습니다. {state.message}
        </EmptyState>
      ) : (
        <HatDataTable
          source={source}
          columns={columns}
          ariaLabel="Raw Checkpoint 목록"
          emptyMessage="이 Session에는 아직 Raw Checkpoint가 없습니다."
          showIndex={false}
          minWidth="0px"
        />
      )}
    </section>
  );
}
